import { useState, useEffect } from "react";
import { X, Loader2, DollarSign, Calendar } from "lucide-react";
import { toast } from "react-hot-toast";
import { quoteJobService } from "../../../services/quoteJobService";

export default function MarkAsPaidModal({ quote, onClose, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [amount, setAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [paidAt, setPaidAt] = useState(new Date().toISOString().split('T')[0]);


    // Calculate remaining balance
    const totalAmount = parseFloat(quote.amount || 0) + parseFloat(quote.report_generation_fees || 0) + parseFloat(quote.quote_fuel || 0);
    const paidAmount = quote.is_paid ? totalAmount : 0;
    const remainingBalance = totalAmount - paidAmount;

    useEffect(() => {
        // Pre-fill amount with remaining balance
        setAmount(remainingBalance.toFixed(2));
    }, [remainingBalance]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!amount || parseFloat(amount) <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }

        if (parseFloat(amount) > remainingBalance) {
            toast.error(`Amount cannot exceed the remaining balance ($${remainingBalance.toFixed(2)})`);
            return;
        }

        setLoading(true);
        try {
            const payload = {
                amount: parseFloat(amount),
                paid_method: paymentMethod,
                paid_at: paidAt
            };

            const response = await quoteJobService.markQuoteAsPaid(quote.id, payload);

            if (response.data.success) {
                toast.success("Payment recorded successfully");
                onSuccess();
            } else {
                toast.error(response.data.message || "Failed to record payment");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Failed to record payment");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900">Record Payment</h3>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 bg-blue-50 border-b border-blue-100">
                    <div className="flex justify-between text-sm mb-1">
                        <span className="text-blue-700 font-medium">Quote Total:</span>
                        <span className="text-blue-900 font-bold">${totalAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                        <span className="text-blue-600">Already Paid:</span>
                        <span className="text-blue-800">${paidAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-blue-200 mt-2">
                        <span className="text-blue-800 font-bold">Remaining Balance:</span>
                        <span className="text-blue-900 font-bold text-lg">${remainingBalance.toFixed(2)}</span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            Payment Amount ($) <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                            <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max={remainingBalance}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                                required
                                disabled
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            Payment Method <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                        >
                            <option value="cash">Cash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="check">Check</option>
                            <option value="credit_card">Credit Card</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            Payment Date <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                            <input
                                type="date"
                                value={paidAt}
                                onChange={(e) => setPaidAt(e.target.value)}
                                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                required
                            />
                        </div>
                    </div>



                    <div className="pt-4 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-70 shadow-sm"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                "Confirm Payment"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
